import React from "react";
import { RoomSettings, RoomRecording } from "../../../entities/room.entity";
import { FiCheck, FiX, FiVideo, FiMic, FiMessageSquare, FiLock } from "react-icons/fi";

type SettingsSectionProps = {
  settings: RoomSettings;
  recording?: RoomRecording;
};

export const SettingsSection: React.FC<SettingsSectionProps> = ({ settings, recording }) => {
  const SettingItem = ({
    icon,
    label,
    enabled,
    description,
  }: {
    icon: React.ReactNode;
    label: string;
    enabled: boolean;
    description?: string;
  }) => (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
      <div className="flex items-center gap-3">
        <div
          className={`p-2 rounded-lg ${
            enabled ? "bg-green-100 text-green-600" : "bg-gray-200 text-gray-500"
          }`}
        >
          {icon}
        </div>
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
      </div>
      <div
        className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
          enabled ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
        }`}
      >
        {enabled ? <FiCheck className="w-4 h-4" /> : <FiX className="w-4 h-4" />}
        {enabled ? "Enabled" : "Disabled"}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SettingItem
          icon={<FiMessageSquare className="w-5 h-5" />}
          label="Allow Chat"
          enabled={settings.allowChat}
          description="Participants can send messages"
        />
        <SettingItem
          icon={<FiMic className="w-5 h-5" />}
          label="Request Mic"
          enabled={settings.requestMic}
          description="Users can request to speak"
        />
      </div>

      {settings.limitedToUserIds?.length > 0 && (
        <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
          <div className="flex items-center gap-2 mb-2">
            <FiLock className="w-4 h-4 text-yellow-600" />
            <p className="font-medium text-yellow-800">Limited Access</p>
          </div>
          <p className="text-sm text-yellow-700">
            Room is limited to {settings.limitedToUserIds.length} specific users
          </p>
        </div>
      )}

      {recording && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
            Recording
          </h4>
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg ${
                    recording.isEnabled
                      ? "bg-red-100 text-red-600"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  <FiVideo className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Room Recording</p>
                  <p className="text-xs text-gray-500">
                    {recording.isEnabled ? "Recording is active" : "Recording is off"}
                  </p>
                </div>
              </div>
              {recording.isEnabled && (
                <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  Recording
                </span>
              )}
            </div>
            {recording.source?.fileUrl && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <a
                  href={recording.source.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  View Recording →
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
